import { useMutation, useQueryClient } from "@tanstack/react-query";

import { useDomainServices } from "@/providers/DomainServices";

import { fileQueryKeys } from "./fileQueryKeys";

interface MutateFileMetadataProps {
  fileId: string;
  putFields?: Record<string, unknown>;
  removeFields?: string[];
}

interface MetadataChangeSet {
  metadata_changeset: {
    put_fields?: Record<string, unknown>;
    remove_fields?: string[];
  };
}

interface MutateMetadataResponse {
  fileId: string;
  metadata: Record<string, unknown>;
}

export function useMutateFileMetadata() {
  const { files } = useDomainServices();
  const queryClient = useQueryClient();

  return useMutation<MutateMetadataResponse, Error, MutateFileMetadataProps>({
    mutationFn: async ({ fileId, putFields, removeFields }) => {
      const body: MetadataChangeSet = {
        metadata_changeset: {},
      };

      if (putFields !== undefined) {
        body.metadata_changeset.put_fields = putFields;
      }

      if (removeFields !== undefined) {
        body.metadata_changeset.remove_fields = removeFields;
      }

      const record = await files.putFileRecord(fileId, {
        body: JSON.stringify(body),
      });

      return {
        fileId: record.file_id,
        metadata: record.metadata,
      };
    },
    onMutate: async (variables) => {
      const { fileId, putFields, removeFields } = variables;
      const queryKey = fileQueryKeys.metadata(fileId);

      // Cancel any outgoing refetches (so they don't overwrite our optimistic update)
      await queryClient.cancelQueries({ queryKey });

      // Optimistically update to the new value
      queryClient.setQueryData<Record<string, unknown>>(queryKey, (old) => {
        const next = { ...old };
        if (putFields) {
          Object.assign(next, putFields);
        }
        if (removeFields) {
          for (const key of removeFields) {
            delete next[key];
          }
        }
        return next;
      });
    },
    onSuccess: (response) => {
      // Update the cache with the updated file metadata from putFileRecord
      queryClient.setQueryData(
        fileQueryKeys.metadata(response.fileId),
        response.metadata,
      );
    },
    onSettled: (_, __, variables) => {
      // Always refetch after error or success to ensure we are in sync with the server.
      return queryClient.invalidateQueries({
        queryKey: fileQueryKeys.metadata(variables.fileId),
      });
    },
  });
}
